import { odooApi } from '../index';
import { Post, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { extractErrorMessage, transformOdooNewsToPost, transformOdooPaginatedResponse } from '../transforms';
import DOMPurify from 'dompurify';

/**
 * Programs API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains program-related API endpoints for various program types:
 * - ISO/Quality programs
 * - Special Needs programs
 * - Women Affairs programs
 * - Volunteering programs
 * - Security/Anti-Doping programs
 * 
 * Connected to real Odoo API at /api/moys/programs/<type>
 */

export type ProgramType = 'iso' | 'specialneeds' | 'women' | 'volunteer' | 'security';

export const programsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get programs by type from Odoo
    getPrograms: builder.query<
      PaginatedResponse<Post>,
      {
        programType: ProgramType;
        page?: number;
        pageSize?: number;
        lang?: 'ar' | 'en' | 'ku';
      }
    >({
      query: ({ programType, page = 1, pageSize = 10, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        
        // Backend expects page and page_size via _get_pagination_params
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });
        
        return {
          url: `/api/moys/programs/${programType}?${params.toString()}`,
          method: 'POST',
        };
      },
      transformResponse: (response: any, meta, arg) => {
        try {
          if (!response.success || !response.data) {
            throw new Error('Invalid response format');
          }
          
          // Use the same transformer as news since structure is identical
          const posts = response.data.map((item: any) => {
            const transformed = transformOdooNewsToPost(item, arg.lang || 'ar');
            
            // Sanitize HTML content
            const sanitizedContent = DOMPurify.sanitize(item.content || '');
            
            return {
              ...transformed,
              bodyRich: {
                ar: sanitizedContent,
                en: sanitizedContent,
                ku: sanitizedContent,
              },
            } as Post;
          });

          // Use the paginated response transformer
          return transformOdooPaginatedResponse(
            response,
            (item: any) => {
              const transformed = transformOdooNewsToPost(item, arg.lang || 'ar');
              const sanitizedContent = DOMPurify.sanitize(item.content || '');
              return {
                ...transformed,
                bodyRich: {
                  ar: sanitizedContent,
                  en: sanitizedContent,
                  ku: sanitizedContent,
                },
              } as Post;
            },
            arg.page || 1,
            arg.pageSize || 10,
            arg.lang || 'ar'
          );
        } catch (error) {
          console.error('[Programs API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result, error, { programType }) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Post' as const, id: `${programType}-${id}` })),
              { type: 'Post', id: `${programType}-LIST` },
            ]
          : [{ type: 'Post', id: `${programType}-LIST` }],
    }),
  }),
});

export const {
  useGetProgramsQuery,
} = programsApi;

